import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const ServicesEdit = ({ service = null, onSuccess = () => {}, onCancel = () => {} }) => {
  const [name, setName] = useState(service?.name ?? "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [price, setPrice] = useState(service?.price ?? "");
  const [isActive, setIsActive] = useState(service?.is_active ?? true);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    setName(service?.name ?? "");
    setDescription(service?.description ?? "");
    setPrice(service?.price ?? "");
    setIsActive(typeof service?.is_active === "boolean" ? service.is_active : true);
  }, [service]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const validate = () => {
    if (!name.trim()) {
      setSnack({ open: true, message: "Name is required", severity: "error" });
      return false;
    }
    if (!price || isNaN(price)) {
      setSnack({ open: true, message: "Valid price is required", severity: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (!service) return;
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        id: service.id,
        name: name.trim(),
        description: description.trim(),
        price: parseFloat(price).toFixed(2),
        is_active: !!isActive,
      };

      const res = await ApiServices.put(`service-offerings/${service.id}/`, payload);
      const data = res?.data ?? res;
      onSuccess("Service updated successfully");
      return data;
    } catch (err) {
      console.error(err);
      let msg = "Failed to update service.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = Object.values(d).flat().join(" ") || msg;
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={3}
        fullWidth
      />
      <TextField
        label="Price"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        required
        type="number"
        inputProps={{ step: "0.01", min: 0 }}
        fullWidth
      />
      <FormControlLabel
        control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
        label="Active"
      />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "#094c9f" }}>
          {loading ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServicesEdit;
